import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Golf, Bed, ForkKnife, Flag } from "@phosphor-icons/react";
import { SEO } from "@/components/SEO";
import { DemoNav, WorkflowStrip, DemoFooter } from "@/components/DemoShell";
import { DemoChat } from "@/components/DemoChat";
import { Reveal, MaskedLine } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PACKAGES = [
    { icon: Golf, name: "Visitor Round", price: "£95", desc: "18 holes on the championship course, practice facilities and clubhouse access." },
    { icon: Bed, name: "Stay & Play", price: "£289", desc: "Overnight suite, breakfast, two rounds and late checkout — the full estate experience." },
    { icon: Flag, name: "Membership", price: "£1,850/yr", desc: "Unlimited golf, member events, dining privileges and priority tee times." },
];

const CHAT = {
    greeting: "Welcome to HIGHTS Golf. I'm the club concierge — tee times, stays, memberships or the restaurant. How can I help?",
    suggestions: [
        { q: "How much is a round?", a: "Visitor rounds are £95 including practice facilities. Twilight rounds from £60. If you're planning more than a few visits, membership at £1,850 a year usually pays for itself — shall I compare options?" },
        { q: "Do you have accommodation?", a: "Yes — 24 suites across the estate, from clubhouse rooms to fairway lodges. Our Stay & Play package at £289 includes a suite, breakfast and two rounds. Would you like me to check dates?" },
        { q: "Can I book a tee time?", a: "Certainly. This week I have Thursday 08:40, Friday 13:20 and Saturday 07:30 for visitors. Which suits you? I'll confirm instantly and send reminders automatically." },
        { q: "What packages are available?", a: "Three favourites: Visitor Round (£95), Stay & Play (£289 with accommodation) and full Membership (£1,850/yr). Corporate and society days are also available — what did you have in mind?" },
    ],
    handoff: "Perfect — I have everything I need. Complete the booking form and the confirmation, reminder and follow-up all run automatically.",
};

export default function GolfDemo() {
    const [form, setForm] = useState({ name: "", email: "", phone: "", date: "", time: "08:40", players: "2" });
    const [state, setState] = useState("idle");
    const [workflow, setWorkflow] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setState("sending");
        try {
            await axios.post(`${API}/enquiries`, {
                name: form.name, email: form.email, phone: form.phone,
                business: "HIGHTS GOLF (demo)", industry: "Golf & hospitality",
                main_challenge: `Tee time request: ${form.date || "flexible"} at ${form.time}, ${form.players} players`,
                desired_outcome: "Golf booking and hospitality enquiry",
                budget: "1000-5000", preferred_contact: "Email", source: "demo-golf",
            });
            setState("success");
            setWorkflow(true);
        } catch {
            setState("error");
            toast.error("Something went wrong — please try again.");
        }
    };

    const inputCls = "h-12 w-full border border-white/15 bg-black px-4 text-sm text-white placeholder:text-white/30 focus:border-white/45 focus:outline-none";
    const labelCls = "mb-2 block text-[11px] font-bold uppercase tracking-[0.2em] text-white/45";

    return (
        <div className="min-h-screen bg-base" data-testid="golf-demo">
            <SEO title="HIGHTS GOLF — Interactive Golf & Hospitality Demo | HIGHTS" description="A working demonstration of a HIGHTS golf and hospitality growth system: tee-time booking, packages, AI concierge and automated guest journeys." path="/demos/golf" />
            <DemoNav name="HIGHTS GOLF" sector="Golf & hospitality demo" />

            <section className="relative flex min-h-[88vh] items-end overflow-hidden pt-16">
                <img src="/assets/img/golf-1.jpg" alt="Championship golf course at dawn" className="absolute inset-0 h-full w-full object-cover opacity-55" />
                <div className="absolute inset-0 bg-gradient-to-t from-base via-black/40 to-black/30" />
                <div className="relative z-10 mx-auto w-full max-w-[1400px] px-5 pb-20 md:px-10">
                    <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-white/60">Championship golf · Estate stays · Dining</p>
                    <h1 className="mt-5 font-display font-black tracking-tighter">
                        <MaskedLine delay={0.2}><span className="block text-5xl leading-[0.95] text-white sm:text-7xl">HIGHTS</span></MaskedLine>
                        <MaskedLine delay={0.4}><span className="block text-5xl leading-[0.95] text-white/55 sm:text-7xl">GOLF</span></MaskedLine>
                    </h1>
                    <Reveal delay={0.6}>
                        <p className="mt-6 max-w-lg text-base font-light leading-relaxed text-white/70">
                            Tee times booked by AI at midnight, memberships renewed automatically, guests followed up after every visit — a hospitality business that never stops working.
                        </p>
                    </Reveal>
                </div>
            </section>

            <section className="py-24" data-testid="golf-packages">
                <div className="mx-auto max-w-[1400px] px-5 md:px-10">
                    <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-white/40">Golf, stays & membership</p>
                    <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-3">
                        {PACKAGES.map((p, i) => (
                            <Reveal key={p.name} delay={i * 0.1}>
                                <div className="group flex h-full flex-col border border-white/12 bg-surface p-8 transition-colors duration-300 hover:border-white/35" data-testid={`golf-package-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                                    <p.icon size={26} weight="light" className="text-white/75" />
                                    <h2 className="mt-6 font-display text-xl font-bold text-white">{p.name}</h2>
                                    <p className="mt-1 font-mono text-sm text-white/60">{p.price}</p>
                                    <p className="mt-4 flex-1 text-sm font-light leading-relaxed text-white/50">{p.desc}</p>
                                    <button
                                        onClick={() => document.getElementById("golf-booking")?.scrollIntoView({ behavior: "smooth" })}
                                        className="mt-8 border border-white/25 py-3 text-[11px] font-bold uppercase tracking-[0.22em] text-white transition-colors hover:bg-white hover:text-black"
                                        data-testid={`golf-enquire-${i}`}
                                    >
                                        Enquire
                                    </button>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                    <Reveal className="mt-6">
                        <div className="relative aspect-[21/9] overflow-hidden">
                            <img src="/assets/img/golf-2.jpg" alt="The clubhouse restaurant in the evening" loading="lazy" className="h-full w-full object-cover opacity-80" />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                            <p className="absolute bottom-5 left-5 font-mono text-[10px] uppercase tracking-[0.3em] text-white/75">The Clubhouse — restaurant & events</p>
                        </div>
                    </Reveal>
                </div>
            </section>

            <section className="border-t border-white/10 bg-void py-24">
                <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-6 px-5 md:px-10 lg:grid-cols-2">
                    <Reveal>
                        <div className="flex h-[560px] flex-col border border-white/12 bg-surface">
                            <DemoChat title="Club Concierge" subtitle="HIGHTS GOLF · live AI" greeting={CHAT.greeting} suggestions={CHAT.suggestions} handoff={CHAT.handoff} live agentId="golf" />
                        </div>
                    </Reveal>
                    <Reveal delay={0.12}>
                        <div id="golf-booking" className="border border-white/12 bg-surface p-8 md:p-10">
                            <h2 className="font-display text-2xl font-black tracking-tight text-white">Book a tee time</h2>
                            <p className="mt-2 text-sm font-light text-white/50">A real enquiry — watch the guest journey automate after you submit.</p>
                            {state === "success" ? (
                                <div className="mt-8 border border-white/15 bg-black/50 p-6" data-testid="golf-booking-success">
                                    <p className="font-display text-lg font-bold text-white">Tee time request received.</p>
                                    <p className="mt-2 text-sm font-light text-white/55">Confirmation is on its way — and the automated journey below just ran.</p>
                                </div>
                            ) : (
                                <form onSubmit={submit} className="mt-8 space-y-5" data-testid="golf-booking-form">
                                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                                        <div>
                                            <label className={labelCls} htmlFor="gd-name">Name *</label>
                                            <input id="gd-name" data-testid="golf-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} placeholder="Your name" />
                                        </div>
                                        <div>
                                            <label className={labelCls} htmlFor="gd-email">Email *</label>
                                            <input id="gd-email" data-testid="golf-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} placeholder="you@email.com" />
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
                                        <div>
                                            <label className={labelCls} htmlFor="gd-date">Date</label>
                                            <input id="gd-date" data-testid="golf-date" type="date" style={{ colorScheme: "dark" }} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={inputCls} />
                                        </div>
                                        <div>
                                            <label className={labelCls} htmlFor="gd-time">Time</label>
                                            <select id="gd-time" data-testid="golf-time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className={inputCls}>
                                                {["07:30", "08:40", "10:10", "13:20", "15:00"].map((t) => <option key={t}>{t}</option>)}
                                            </select>
                                        </div>
                                        <div>
                                            <label className={labelCls} htmlFor="gd-players">Players</label>
                                            <select id="gd-players" data-testid="golf-players" value={form.players} onChange={(e) => setForm({ ...form, players: e.target.value })} className={inputCls}>
                                                {["1", "2", "3", "4"].map((n) => <option key={n}>{n}</option>)}
                                            </select>
                                        </div>
                                    </div>
                                    <button type="submit" disabled={state === "sending"} data-testid="golf-submit"
                                        className="w-full bg-white py-4 text-xs font-bold uppercase tracking-[0.25em] text-black transition-colors hover:bg-white/85 disabled:opacity-50">
                                        {state === "sending" ? "Sending…" : "Request tee time"}
                                    </button>
                                </form>
                            )}
                            <div className="mt-8">
                                <WorkflowStrip active={workflow} />
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>
            <DemoFooter name="HIGHTS GOLF" />
        </div>
    );
}
